<template>
  <v-container class="mt-4">
    <h2 class="text-h4 mb-4">Тест конфигурации навигации</h2>
    
    <v-row>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Боковое меню (Sidebar)</v-card-title>
          <v-card-text>
            <v-list v-if="sidebarNavigation.length > 0" density="compact">
              <template v-for="item in sidebarNavigation" :key="item.id">
                <v-list-item>
                  <template v-slot:prepend>
                    <v-icon :icon="convertIcon(item.icon)"></v-icon>
                  </template>
                  <v-list-item-title>
                    <strong>{{ item.title }}</strong>
                    <v-chip size="x-small" class="ml-2">{{ item.roles.join(', ') }}</v-chip>
                  </v-list-item-title>
                </v-list-item>
                <v-list v-if="item.children && item.children.length > 0" class="ml-4">
                  <v-list-item v-for="child in item.children" :key="child.id" :to="child.route">
                    <v-list-item-title>
                      {{ child.title }}
                      <v-chip size="x-small" variant="outlined" class="ml-2">{{ child.roles.join(', ') }}</v-chip>
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </template>
            </v-list>
            <div v-else class="text-grey">
              Нет доступных пунктов меню
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Верхнее меню (Header)</v-card-title>
          <v-card-text>
            <v-list v-if="headerNavigation.length > 0" density="compact">
              <v-list-item v-for="item in headerNavigation" :key="item.id" :to="item.route">
                <v-list-item-title>
                  {{ item.title }}
                  <v-chip size="x-small" class="ml-2">
                    {{ item.roles.length > 0 ? item.roles.join(', ') : 'public' }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item>
            </v-list>
            <div v-else class="text-grey">
              Нет доступных пунктов меню
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <v-card class="mt-4">
      <v-card-title>Тест доступа к маршрутам</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <div class="text-h6 mb-3">Проверить маршрут:</div>
            <v-text-field
              v-model="testRoute"
              placeholder="/admin/users"
              variant="outlined"
              density="compact"
              append-inner-icon="mdi-magnify"
              @click:append-inner="checkRouteAccess"
            >
              <template v-slot:append>
                <v-btn color="primary" @click="checkRouteAccess">Проверить</v-btn>
              </template>
            </v-text-field>
            <v-alert 
              v-if="routeCheckResult !== null" 
              :type="routeCheckResult ? 'success' : 'error'"
              class="mt-3"
            >
              {{ routeCheckResult ? '✓ Доступ разрешен' : '✗ Доступ запрещен' }}
            </v-alert>
          </v-col>
          
          <v-col cols="12" md="6">
            <div class="text-h6 mb-3">Защищенные маршруты:</div>
            <v-list density="compact">
              <v-list-item v-for="(roles, route) in protectedRoutes" :key="route">
                <v-list-item-title>
                  <code>{{ route }}</code>
                  <v-chip size="x-small" color="info" class="ml-2">{{ roles.join(', ') }}</v-chip>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    
    <v-card class="mt-4">
      <v-card-title>Тест фильтрации по ролям</v-card-title>
      <v-card-text>
        <div class="mb-3">
          <v-btn
            size="small"
            variant="outlined"
            color="primary"
            class="mr-2"
            @click="simulateRole([])"
          >
            Без ролей
          </v-btn>
          <v-btn
            size="small"
            variant="outlined"
            color="primary"
            class="mr-2"
            @click="simulateRole(['user'])"
          >
            Роль: user
          </v-btn>
          <v-btn
            size="small"
            variant="outlined"
            color="primary"
            class="mr-2"
            @click="simulateRole(['admin'])"
          >
            Роль: admin
          </v-btn>
          <v-btn
            size="small"
            variant="outlined"
            color="primary"
            @click="simulateRole(['user', 'admin'])"
          >
            Роли: user + admin
          </v-btn>
        </div>
        <v-sheet v-if="simulatedNavigation" border rounded class="pa-3">
          <div class="text-h6 mb-3">Результат фильтрации ({{ simulatedRoles.join(', ') || 'без ролей' }}):</div>
          <v-list density="compact">
            <template v-for="item in simulatedNavigation" :key="item.id">
              <v-list-item>
                <v-list-item-title>
                  <strong>{{ item.title }}</strong>
                </v-list-item-title>
              </v-list-item>
              <v-list v-if="item.children && item.children.length > 0" class="ml-4">
                <v-list-item v-for="child in item.children" :key="child.id">
                  <v-list-item-title>{{ child.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </template>
          </v-list>
        </v-sheet>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import { 
  filterNavigationByRoles, 
  getProtectedRoutes,
  navigationConfig 
} from '@/config/navigation';

export default {
  name: 'TestNavigation',
  data() {
    return {
      testRoute: '/admin/users',
      routeCheckResult: null,
      simulatedNavigation: null,
      simulatedRoles: []
    };
  },
  computed: {
    ...mapGetters('navigation', ['sidebarNavigation', 'headerNavigation', 'canAccessRoute']),
    ...mapGetters(['isAuthenticated', 'userRoles']),
    protectedRoutes() {
      return getProtectedRoutes();
    }
  },
  methods: {
    checkRouteAccess() {
      this.routeCheckResult = this.canAccessRoute(this.testRoute);
    },
    
    simulateRole(roles) {
      this.simulatedRoles = roles;
      this.simulatedNavigation = filterNavigationByRoles(
        navigationConfig.sidebar, 
        roles, 
        true // Assume authenticated for testing
      );
    },
    
    convertIcon(bootstrapIcon) {
      // Map Bootstrap icons to Material Design Icons
      const iconMap = {
        'bi bi-speedometer2': 'mdi-speedometer',
        'bi bi-folder': 'mdi-folder',
        'bi bi-telegram': 'mdi-send',
        'bi bi-search': 'mdi-magnify',
        'bi bi-shield-lock': 'mdi-shield-lock',
        'bi bi-people': 'mdi-account-multiple',
        'bi bi-activity': 'mdi-chart-line',
        'bi bi-database': 'mdi-database',
        'bi bi-key': 'mdi-key',
        'bi bi-bug': 'mdi-bug'
      };
      
      return iconMap[bootstrapIcon] || 'mdi-circle';
    }
  }
};
</script>

<style scoped>
code {
  background-color: #f5f5f5;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: monospace;
}
</style>